import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle2,
  ClipboardCopy,
  Clock,
  ExternalLink,
  HandCoins,
  ReceiptText,
} from "lucide-react";
import {
  acknowledgeCommercialPolicy,
  canonicalTermsPayload,
  ensureCommercialCloseOpportunity,
  getCurrentUserId,
  issueAttributedCheckout,
  listCommercialApprovals,
  quoteCommercialTerms,
  requestDiscountApproval,
  upsertCaseStudyAgreement,
  type CanonicalTermsFields,
  type CommercialPlan,
  type CommercialQuoteResult,
  type DiscountApprovalRequest,
  type CommercialApprovalRow,
} from "./lib/api";
import { config } from "./lib/config";
import {
  beginCheckoutAttempt,
  completeCheckoutAttempt,
  createCheckoutAttempt,
  extractAttributionRef,
  failCheckoutAttempt,
  hasReachableContactEmail,
  providerCapabilityState,
  redactForDisplay,
  resetCheckoutAttempt,
  secondsRemaining,
  sessionExpiryState,
} from "./lib/paymentDesk";
import {
  opportunitiesForOrganization,
  resolveCommercialReadiness,
  resolveOpportunityLifecycle,
} from "./lib/commercialCloseState";
import type { Contact, Organization, Opportunity } from "./types";

const PROGRAM_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ["NONE", "None"],
  ["CASE_STUDY_PARTNER", "Case Study Partner"],
  ["EARLY_ADOPTER", "Early Adopter"],
  ["MULTI_LOCATION", "Multi-Location"],
  ["STRATEGIC_ACCOUNT", "Strategic Account"],
  ["RETENTION_RECOVERY", "Retention Recovery"],
  ["EXECUTIVE_APPROVED", "Executive Approved"],
];

const REQUIRED_TIER_LABEL: Record<string, string> = {
  NONE: "NONE REQUIRED",
  TIER1: "SALES MANAGER APPROVAL REQUIRED",
  TIER2: "SALES ADMIN APPROVAL REQUIRED",
  EXECUTIVE: "EXECUTIVE APPROVAL REQUIRED",
};

const QUOTE_ERROR_COPY: Record<string, string> = {
  STANDARD_DISCOUNT_BLOCKED:
    "Above the 20% standard maximum — requires a Custom Commercial Agreement",
  PROGRAM_MAXIMUM_EXCEEDED:
    "That program allows a smaller maximum discount than entered.",
  PROGRAM_UNAVAILABLE:
    "That program is not currently active on this policy version.",
  AGREEMENT_REQUIRED:
    "This program requires an agreed case study agreement before discounted terms can be requested.",
  ACKNOWLEDGEMENT_REQUIRED:
    "Read and acknowledge the Commercial Terms, Discount & Commission Policy before requesting approval.",
};

const REQUEST_ERROR_COPY: Record<string, string> = {
  ...QUOTE_ERROR_COPY,
  TERMS_HASH_MISMATCH:
    "Commercial terms changed — new approval required",
  TERMS_HASH_REQUIRED:
    "Commercial terms changed — new approval required",
  OPPORTUNITY_UNAVAILABLE:
    "The opportunity must be open and belong to this organization.",
  CONTACT_UNAVAILABLE:
    "The contact must belong to the bound organization.",
  ORGANIZATION_UNAVAILABLE: "The organization could not be verified.",
  invalid_discount_reason: "A short reason for the discount is required.",
  invalid_supporting_note: "The supporting note is too long.",
};

const ISSUE_ERROR_COPY: Record<string, string> = {
  guidance_offer_not_current: "Confirm the current fitting offer and the buyer's acceptance in the guide before issuing checkout.",
  CHECKOUT_DENIED_APPROVAL_REQUIRED:
    "Commercial terms changed — new approval required",
  APPROVAL_INVALIDATED: "Commercial terms changed — new approval required",
  TERMS_HASH_MISMATCH: "Commercial terms changed — new approval required",
  APPROVAL_SCOPE_MISMATCH: "Commercial terms changed — new approval required",
  ATTRIBUTION_MISMATCH:
    "Attribution mismatch — this opportunity is attributed to a different rep.",
  self_approval_prohibited: "You cannot approve your own request.",
  approval_expired:
    "The approval expired before checkout was issued. Request a new approval.",
  checkout_idempotency_conflict:
    "A checkout session already exists for this attempt. Start a new one.",
};

const PROVIDER_PENDING_BANNER =
  "Provider pending — payment capture is not available in this desk yet. Verified payments arrive from the provider webhook.";

function formatCents(cents: number): string {
  const value = cents / 100;
  return `${value < 0 ? "-" : ""}$${Math.abs(value)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function formatCountdown(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

export type CommercialClosePanelProps = {
  contact: Contact | null;
  organizations: Organization[];
  opportunities: Opportunity[];
  callSessionId: string | null;
  onOpportunitiesChanged?: () => void;
  prescribedPlan?: CommercialPlan | null;
  prescribedQuantity?: number | null;
  prescribedProgram?: string | null;
  prescribedDiscountBps?: number | null;
  onQuantitySelected?: (quantity:number|null) => void;
  onPlanSelected?: (plan: CommercialPlan) => void;
  onTermsChanged?: (plan: CommercialPlan, monthlyPrice: number | null, context?: {quantity:number;discountBps:number;program:string;policyVersion:string|null}) => void;
  onQuoteObserved?: (observation: {contactId:string|null;plan:CommercialPlan;price:number|null;quantity:number;discountBps:number;program:string;policyVersion:string|null;ready:boolean}) => void;
  onExternalBoundary?: (reason:string,status:'pending'|'uncertain'|'completed') => Promise<void>;
};

export function CommercialClosePanel({
  contact,
  organizations,
  opportunities,
  callSessionId,
  onOpportunitiesChanged,
  prescribedPlan,
  prescribedQuantity,
  prescribedProgram,
  prescribedDiscountBps,
  onQuantitySelected,
  onPlanSelected,
  onTermsChanged,
  onQuoteObserved,
  onExternalBoundary,
}: CommercialClosePanelProps) {
  const externalActionLock=useRef(false);
  const performExternalAction=useCallback(async <T,>(reason:string,mutation:()=>Promise<T>):Promise<T>=>{
    if(externalActionLock.current)throw new Error('commercial_action_already_pending');
    externalActionLock.current=true;
    try{
      await onExternalBoundary?.(reason,'pending');
      try{const result=await mutation();await onExternalBoundary?.(reason,'completed');return result}
      catch(error){try{await onExternalBoundary?.(reason,'uncertain')}catch{/* Persisted pending boundary remains protective. */}throw error}
    }finally{externalActionLock.current=false}
  },[onExternalBoundary]);
  const organization = useMemo(
    () =>
      contact?.organizationId
        ? organizations.find((item) => item.id === contact.organizationId) ||
          null
        : null,
    [contact, organizations],
  );
  const [ensuredOpportunity, setEnsuredOpportunity] =
    useState<Opportunity | null>(null);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState("");
  const [creatingOpportunity, setCreatingOpportunity] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const organizationOpportunities = useMemo(
    () =>
      opportunitiesForOrganization(
        contact?.organizationId,
        opportunities,
        ensuredOpportunity,
      ),
    [contact?.organizationId, ensuredOpportunity, opportunities],
  );
  const opportunityLifecycle = useMemo(
    () =>
      resolveOpportunityLifecycle(
        organizationOpportunities,
        selectedOpportunityId,
        creatingOpportunity,
      ),
    [creatingOpportunity, organizationOpportunities, selectedOpportunityId],
  );
  const openOpportunities = opportunityLifecycle.openOpportunities;
  const boundOpportunity = opportunityLifecycle.boundOpportunity;

  useEffect(() => {
    setSelectedOpportunityId("");
    setEnsuredOpportunity(null);
    setCreateError(null);
  }, [contact?.id]);

  useEffect(() => {
    if (openOpportunities.length === 1) {
      setSelectedOpportunityId(openOpportunities[0].id);
    } else if (
      selectedOpportunityId &&
      !openOpportunities.some((item) => item.id === selectedOpportunityId)
    ) {
      setSelectedOpportunityId("");
    }
  }, [openOpportunities, selectedOpportunityId]);

  const [plan, setPlan] = useState<CommercialPlan>("watch");
  const [locationsValue, setLocationsValue] = useState("1");
  const [program, setProgram] = useState("NONE");
  const [discountPercentValue, setDiscountPercentValue] = useState("");

  const locations = Number(locationsValue);
  const discountBps = Math.round(Number(discountPercentValue || 0) * 100);
  const inputsValid =
    Number.isInteger(locations) &&
    locations >= 1 &&
    locations <= 10000 &&
    Number.isInteger(discountBps) &&
    discountBps >= 0 &&
    discountBps <= 10000;

  const [quote, setQuote] = useState<CommercialQuoteResult | null>(null);
  const [quoteKey, setQuoteKey] = useState("");
  const [quoteContactId, setQuoteContactId] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteErrorCode, setQuoteErrorCode] = useState<string | null>(null);
  const lastGuidanceConfirmation = useRef<string | null>(null);
  const currentQuoteScope = useRef("");
  currentQuoteScope.current=JSON.stringify([contact?.id,plan,locations,discountBps,program]);
  useEffect(()=>{lastGuidanceConfirmation.current=null});
  const currentQuoteReady = Boolean(contact && inputsValid && quote && quoteContactId===contact.id && quoteKey===`${plan}:${locations}:${discountBps}:${program}` && !quoteLoading && !quoteErrorCode && Number.isFinite(quote.netCents) && quote.netCents>0 && Number.isInteger(quote.policyVersion) && quote.policyVersion>0);
  const approvalRequired = Boolean(quote && quote.requiredTier !== "NONE");
  const [ackRequired, setAckRequired] = useState(false);
  const [agreementRequested, setAgreementRequested] = useState(false);
  const [agreementPending, setAgreementPending] = useState(false);

  useEffect(() => {
    setPlan("watch");
    setLocationsValue("1");
    setProgram("NONE");
    setDiscountPercentValue("");
    setQuote(null);
    setQuoteKey("");
    setQuoteContactId(null);
    lastGuidanceConfirmation.current=null;
    setQuoteErrorCode(null);
    setAgreementRequested(false);
    setAgreementPending(false);
  }, [contact?.id]);

  useEffect(()=>{if(prescribedPlan)setPlan(prescribedPlan)},[contact?.id,prescribedPlan]);
  useEffect(()=>{if(prescribedQuantity&&Number.isInteger(prescribedQuantity))setLocationsValue(String(prescribedQuantity))},[contact?.id,prescribedQuantity]);
  useEffect(()=>{setProgram(PROGRAM_OPTIONS.some(([value])=>value===prescribedProgram)?prescribedProgram!:'NONE')},[contact?.id,prescribedProgram]);
  useEffect(()=>{setDiscountPercentValue(prescribedDiscountBps!=null&&Number.isInteger(prescribedDiscountBps)&&prescribedDiscountBps>=0&&prescribedDiscountBps<=10000?String(prescribedDiscountBps/100):'')},[contact?.id,prescribedDiscountBps]);
  useEffect(()=>{
    onQuoteObserved?.({contactId:contact?.id??null,plan,price:currentQuoteReady&&quote?quote.netCents/100:null,quantity:locations,discountBps,program,policyVersion:currentQuoteReady&&quote?String(quote.policyVersion):null,ready:currentQuoteReady});
  },[onQuoteObserved,contact?.id,plan,currentQuoteReady,quote,locations,discountBps,program]);

  const invalidateGuidanceTerms = (nextDiscountBps:number,nextProgram:string) => {
    lastGuidanceConfirmation.current=null;
    setQuote(null);
    setQuoteKey("");
    onTermsChanged?.(plan,null,{quantity:locations,discountBps:nextDiscountBps,program:nextProgram,policyVersion:null});
  };
  const confirmGuidanceQuote = () => {
    if(!currentQuoteReady||!quote||!onTermsChanged)return;
    const context={quantity:locations,discountBps,program,policyVersion:String(quote.policyVersion)};
    const confirmation=JSON.stringify([contact?.id,plan,quote.netCents,context]);
    if(lastGuidanceConfirmation.current===confirmation)return;
    lastGuidanceConfirmation.current=confirmation;
    onTermsChanged(plan,quote.netCents/100,context);
  };

  useEffect(() => {
    if (!inputsValid) {
      setQuote(null);
      setQuoteErrorCode(null);
      return;
    }
    let active = true;
    setQuoteLoading(true);
    const timer = window.setTimeout(() => {
      quoteCommercialTerms({ plan, locations, discountBps, program })
        .then((value) => {
          if (active) {
            setQuote(value);
            setQuoteContactId(contact?.id??null);
            setQuoteKey(`${plan}:${locations}:${discountBps}:${program}`);
            setQuoteErrorCode(null);
          }
        })
        .catch((error: unknown) => {
          if (active) {
            setQuote(null);
            setQuoteErrorCode(
              error instanceof Error ? error.message : "quote_failed",
            );
          }
        })
        .finally(() => {
          if (active) setQuoteLoading(false);
        });
    }, 400);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [contact?.id, plan, locations, discountBps, program, inputsValid]);

  const [reason, setReason] = useState("");
  const [supportingNote, setSupportingNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestErrorCode, setRequestErrorCode] = useState<string | null>(null);
  const [approvalRequest, setApprovalRequest] =
    useState<DiscountApprovalRequest | null>(null);
  const [decidedRow, setDecidedRow] = useState<CommercialApprovalRow | null>(
    null,
  );

  const refreshQuote = useCallback(() => {
    if (!inputsValid) return;
    const requestedScope=JSON.stringify([contact?.id,plan,locations,discountBps,program]);
    quoteCommercialTerms({ plan, locations, discountBps, program })
      .then((value) => {
        if(currentQuoteScope.current!==requestedScope)return;
        setQuote(value);
        setQuoteKey(`${plan}:${locations}:${discountBps}:${program}`);
        setQuoteContactId(contact?.id??null);
        setQuoteErrorCode(null);
      })
      .catch((error: unknown) => {
        if(currentQuoteScope.current!==requestedScope)return;
        setQuote(null);
        setQuoteErrorCode(
          error instanceof Error ? error.message : "quote_failed",
        );
      });
  }, [contact?.id,plan, locations, discountBps, program, inputsValid]);

  const submitApprovalRequest = useCallback(() => {
    if (
      !contact ||
      !organization ||
      !boundOpportunity ||
      !quote ||
      quote.requiredTier === "NONE" ||
      submitting
    ) {
      return;
    }
    if (reason.trim().length < 1) {
      setRequestErrorCode("invalid_discount_reason");
      return;
    }
    setSubmitting(true);
    setRequestErrorCode(null);
    setAckRequired(false);
    performExternalAction('Actual discount approval request',()=>requestDiscountApproval({
      organizationId: organization.id,
      contactId: contact.id,
      opportunityId: boundOpportunity.id,
      callSessionId: callSessionId ?? null,
      plan,
      locations,
      program,
      discountBps,
      durationMonths: null,
      reason: reason.trim(),
      supportingNote: supportingNote.trim() || null,
      quote,
    }))
      .then((value) => {
        setApprovalRequest(value);
        setDecidedRow(null);
        return listCommercialApprovals("ALL", true, 50)
          .then((rows) => {
            const row = rows.find((item) => item.id === value.id);
            if (row && row.status !== "PENDING_APPROVAL") setDecidedRow(row);
          })
          .catch(() => undefined);
      })
      .catch((error: unknown) => {
        const code =
          error instanceof Error ? error.message : "discount_request_rejected";
        if (code === "TERMS_HASH_MISMATCH" || code === "TERMS_HASH_REQUIRED") {
          refreshQuote();
          setRequestErrorCode("TERMS_HASH_MISMATCH");
        } else if (code === "ACKNOWLEDGEMENT_REQUIRED") {
          setAckRequired(true);
        } else {
          setRequestErrorCode(code);
        }
      })
      .finally(() => setSubmitting(false));
  }, [
    boundOpportunity,
    callSessionId,
    contact,
    discountBps,
    locations,
    organization,
    plan,
    program,
    quote,
    reason,
    refreshQuote,
    supportingNote,
    submitting,
    performExternalAction,
  ]);

  useEffect(() => {
    if (!approvalRequired) return;
    if (!approvalRequest || decidedRow) return;
    let active = true;
    const poll = () => {
      listCommercialApprovals("ALL", true, 50)
        .then((rows) => {
          if (!active) return;
          const row = rows.find((item) => item.id === approvalRequest.id);
          if (row && row.status !== "PENDING_APPROVAL") setDecidedRow(row);
        })
        .catch(() => undefined);
    };
    const timer = window.setInterval(poll, 10_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [approvalRequest, approvalRequired, decidedRow]);

  // Re-discover a previously APPROVED request after remount: without this, an
  // approved approval is invisible to the panel and the rep is forced to
  // re-request identical terms. Matching is exact-scope; server remains the
  // authority at issuance (status/expiry/consumption/terms hash re-verified).
  useEffect(() => {
    if (!approvalRequired) return;
    if (approvalRequest || decidedRow) return;
    if (!contact || !organization || !boundOpportunity || !quote) return;
    let active = true;
    listCommercialApprovals("ALL", true, 50)
      .then((rows) => {
        if (!active) return;
        const now = Date.now();
        const match = rows.find(
          (row) =>
            row.status === "APPROVED" &&
            new Date(row.expiresAt).getTime() > now &&
            row.opportunityId === boundOpportunity.id &&
            row.plan === plan &&
            row.locationCount === locations &&
            row.program === program &&
            row.discountBps === discountBps &&
            row.listTotalCents === quote.listTotalCents &&
            row.netCents === quote.netCents,
        );
        if (match) setDecidedRow(match);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [
    approvalRequest,
    approvalRequired,
    decidedRow,
    boundOpportunity,
    contact,
    organization,
    plan,
    locations,
    program,
    discountBps,
    quote,
  ]);

  useEffect(() => {
    setApprovalRequest(null);
    setDecidedRow(null);
    setRequestErrorCode(null);
    setAckRequired(false);
    setReason("");
    setSupportingNote("");
  }, [boundOpportunity?.id, contact?.id]);

  async function acknowledgeThenRetry() {
    setAckRequired(false);
    try {
      await performExternalAction('Actual commercial policy acknowledgement',()=>acknowledgeCommercialPolicy());
    } catch {
      setAckRequired(true);
      return;
    }
    submitApprovalRequest();
  }

  async function requestCaseStudyAgreement() {
    if (!organization || agreementPending) return;
    setAgreementPending(true);
    try {
      await performExternalAction('Actual case-study agreement request',()=>upsertCaseStudyAgreement({
        organizationId: organization.id,
        signerContactId: contact?.id ?? null,
      }));
      setAgreementRequested(true);
    } catch {
      setRequestErrorCode("AGREEMENT_REQUIRED");
    } finally {
      setAgreementPending(false);
    }
  }

  async function createOpportunity() {
    if (!contact || !organization || creatingOpportunity) return;
    setCreatingOpportunity(true);
    setCreateError(null);
    try {
      const ownerUserId = await getCurrentUserId();
      const result = await performExternalAction('Actual opportunity creation',()=>ensureCommercialCloseOpportunity({
        opportunityId: crypto.randomUUID(),
        organizationId: organization.id,
        contactId: contact.id,
        ownerUserId,
        name: `${organization.name} — close ${new Date().toISOString().slice(0, 10)}`,
      }));
      setEnsuredOpportunity(result.opportunity);
      setSelectedOpportunityId(result.opportunity.id);
      onOpportunitiesChanged?.();
    } catch (error) {
      const code =
        error instanceof Error ? error.message : "opportunity_create_failed";
      setCreateError(code);
    } finally {
      setCreatingOpportunity(false);
    }
  }

  const liveApproval = decidedRow;
  const termsDrift = useMemo(() => {
    if (!approvalRequired || !liveApproval || !quote) return false;
    return (
      liveApproval.plan !== plan ||
      liveApproval.locationCount !== locations ||
      liveApproval.program !== program ||
      liveApproval.discountBps !== discountBps ||
      liveApproval.listTotalCents !== quote.listTotalCents ||
      liveApproval.netCents !== quote.netCents ||
      liveApproval.durationMonths !== null
    );
  }, [approvalRequired, discountBps, liveApproval, locations, plan, program, quote]);

  const takePaymentState = providerCapabilityState();
  const [issuedUrl, setIssuedUrl] = useState<string | null>(null);
  const [checkoutSession, setCheckoutSession] = useState<{
    attributionRef: string;
    expiresAt: string;
  } | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [issueErrorCode, setIssueErrorCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const attemptRef = useRef(createCheckoutAttempt(crypto.randomUUID()));

  useEffect(() => {
    attemptRef.current = resetCheckoutAttempt(crypto.randomUUID());
    setIssuedUrl(null);
    setCheckoutSession(null);
    setIssueErrorCode(null);
  }, [boundOpportunity?.id, contact?.id]);

  useEffect(() => {
    if (!checkoutSession) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [checkoutSession]);

  const buildCanonicalTerms = useCallback((): string | null => {
    if (!quote) return null;
    const fields: CanonicalTermsFields = {
      workspaceId: config.workspaceId,
      organizationId: organization?.id ?? null,
      contactId: contact?.id ?? null,
      opportunityId: boundOpportunity?.id ?? null,
      callSessionId: callSessionId ?? null,
      plan,
      locationCount: locations,
      program,
      discountBps,
      durationMonths: null,
      listTotalCents: quote.listTotalCents,
      discountCents: quote.discountCents,
      netCents: quote.netCents,
      baseCommissionBps: quote.baseCommissionBps,
      baseCommissionCents: quote.baseCommissionCents,
      repDiscountShareCents: quote.repDiscountShareCents,
      adjustedCommissionCents: quote.adjustedCommissionCents,
      effectiveRateBps: quote.effectiveRateBps,
      companyRetainedCents: quote.companyRetainedCents,
      requiredTier: quote.requiredTier,
      standardMaxBps: quote.standardMaxBps,
      programMaxBps: quote.programMaxBps,
      policyVersion: quote.policyVersion,
    };
    return canonicalTermsPayload(fields);
  }, [
    boundOpportunity?.id,
    callSessionId,
    contact?.id,
    discountBps,
    locations,
    organization?.id,
    plan,
    program,
    quote,
  ]);

  async function issueCheckout() {
    if (
      !contact ||
      !organization ||
      !boundOpportunity ||
      (approvalRequired &&
        (!liveApproval || liveApproval.status !== "APPROVED" || termsDrift)) ||
      issuing ||
      attemptRef.current.status === "ISSUING"
    ) {
      return;
    }
    const canonical = buildCanonicalTerms();
    if (!canonical) return;
    setIssuing(true);
    setIssueErrorCode(null);
    setCopied(false);
    setCopyFailed(false);
    attemptRef.current = beginCheckoutAttempt(attemptRef.current);
    try {
      const termsHash = await sha256Hex(canonical);
      const result = await performExternalAction('Actual attributed checkout issue',()=>issueAttributedCheckout({
        organizationId: organization.id,
        contactId: contact.id,
        opportunityId: boundOpportunity.id,
        planId: plan,
        locationCount: locations,
        idempotencyKey: attemptRef.current.idempotencyKey,
        approvalId:
          approvalRequired && liveApproval?.status === "APPROVED"
            ? liveApproval.id
            : undefined,
        termsHash:
          approvalRequired && liveApproval?.status === "APPROVED"
            ? termsHash
            : undefined,
        canonicalTerms:
          approvalRequired && liveApproval?.status === "APPROVED"
            ? canonical
            : undefined,
        callSessionId: callSessionId ?? undefined,
      }));
      const attributionRef = extractAttributionRef(result.checkoutUrl);
      if (!attributionRef) throw new Error("invalid_checkout_reference");
      attemptRef.current = completeCheckoutAttempt(attemptRef.current);
      setNow(Date.now());
      setIssuedUrl(result.checkoutUrl);
      setCheckoutSession({ attributionRef, expiresAt: result.expiresAt });
    } catch (error) {
      attemptRef.current = failCheckoutAttempt(attemptRef.current);
      setIssueErrorCode(
        error instanceof Error ? error.message : "checkout_issue_failed",
      );
    } finally {
      setIssuing(false);
    }
  }

  async function copyIssuedUrl() {
    if (!issuedUrl) return;
    try {
      await navigator.clipboard.writeText(issuedUrl);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  }

  const expiryState = checkoutSession
    ? sessionExpiryState(checkoutSession.expiresAt, now)
    : null;
  const remainingSeconds = checkoutSession
    ? secondsRemaining(checkoutSession.expiresAt, now)
    : 0;
  const maskedReference = checkoutSession
    ? redactForDisplay(checkoutSession).attributionRef
    : null;

  const approvalStatus = liveApproval?.status ?? approvalRequest?.status ?? null;
  const readiness = resolveCommercialReadiness({
    hasContact: Boolean(contact),
    hasOrganization: Boolean(organization),
    hasOpportunity: Boolean(boundOpportunity),
    inputsValid,
    quoteLoading,
    hasQuote: Boolean(quote),
    quoteError: Boolean(quoteErrorCode),
    approvalRequired,
    approvalStatus,
    termsDrift,
    hasReachableEmail: hasReachableContactEmail(contact?.email),
  });

  return (
    <section className="commercial-close close-panel panel">
      <details
        key={contact?.id ?? "no-contact"}
        className="close-details"
        open={Boolean(contact)}
      >
        <summary className="close-summary">
          <HandCoins size={15} />
          <span className="close-summary-title">CLOSE / PAYMENT</span>
          {organization && (
            <span className="close-summary-org">{organization.name}</span>
          )}
          {contact && organization && (
            <span
              className="close-summary-status"
              data-lifecycle-state={opportunityLifecycle.state}
            >
              {opportunityLifecycle.label}
            </span>
          )}
        </summary>
        <div className="close-body">
          <Link className="policy-link close-policy-link" to="/sales-knowledge">
            View Commercial Policy
          </Link>
          {!contact && (
            <p className="empty-state">Select a customer to begin closing</p>
          )}
          {contact && !organization && (
            <div className="runtime-banner issue" role="alert">
              <AlertTriangle size={16} />
              <span>
                This contact is not linked to an organization yet, so commercial
                terms cannot be bound truthfully.
              </span>
            </div>
          )}
          {contact && organization && (
            <>
              <div className="close-opportunity-block" aria-live="polite">
                <div
                  className="close-lifecycle-state"
                  data-lifecycle-state={opportunityLifecycle.state}
                >
                  <strong>{opportunityLifecycle.label}</strong>
                  <span>{opportunityLifecycle.detail}</span>
                </div>
                {[
                  "NO_OPPORTUNITY",
                  "CLOSED_WON",
                  "CLOSED_LOST",
                  "OPPORTUNITY_BEING_CREATED",
                ].includes(opportunityLifecycle.state) && (
                  <div className="close-create-opp">
                    <button
                      type="button"
                      className="secondary-btn"
                      disabled={creatingOpportunity}
                      onClick={() => void createOpportunity()}
                    >
                      {creatingOpportunity
                        ? "Confirming opportunity…"
                        : "Create opportunity"}
                    </button>
                    <small>
                      This safely reuses an existing open opportunity or creates
                      one Discovery opportunity for {organization.name}.
                    </small>
                  </div>
                )}
                {opportunityLifecycle.state === "OPEN_OPPORTUNITY" &&
                  boundOpportunity && (
                  <small className="auto-bind-caption">
                    Bound to {boundOpportunity.name} · {boundOpportunity.stage}
                  </small>
                )}
                {opportunityLifecycle.state === "OPPORTUNITY_AVAILABLE" && (
                  <label className="opp-select">
                    Choose opportunity
                    <select
                      value={selectedOpportunityId}
                      onChange={(event) =>
                        setSelectedOpportunityId(event.target.value)
                      }
                    >
                      <option value="">Choose opportunity…</option>
                      {openOpportunities.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} · {item.stage}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {createError && (
                <p role="alert" className="close-inline-error">
                  The opportunity could not be confirmed. Refresh and try again.
                </p>
              )}

              <div className="close-controls-grid">
                <div className="control-block">
                  <span className="control-label">PLAN</span>
                  <div
                    className="plan-segmented"
                    role="group"
                    aria-label="Canonical plan"
                  >
                    {(["watch", "protect", "shield"] as CommercialPlan[]).map(
                      (value) => (
                        <button
                          key={value}
                          type="button"
                          className={`plan-chip${plan === value ? " active" : ""}`}
                          onClick={() => {if(value!==plan){lastGuidanceConfirmation.current=null;setQuote(null);setQuoteKey("");setPlan(value);onPlanSelected?.(value)}}}
                        >
                          {value.charAt(0).toUpperCase() + value.slice(1)}
                        </button>
                      ),
                    )}
                  </div>
                </div>
                <label className="control-block loc-input">
                  <span className="control-label">LOCATIONS</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={locationsValue}
                    onChange={(event) => {
                      const next=event.target.value.replace(/[^0-9]/g, "");setLocationsValue(next);
                      const nextQuantity=next&&Number(next)>=1&&Number(next)<=10000?Number(next):null;
                      const currentQuantity=locations>=1&&locations<=10000?locations:null;
                      if(nextQuantity!==currentQuantity){lastGuidanceConfirmation.current=null;setQuote(null);setQuoteKey("");onQuantitySelected?.(nextQuantity)}
                    }}
                  />
                </label>
                <label className="control-block program-select">
                  <span className="control-label">PROGRAM</span>
                  <select
                    value={program}
                    onChange={(event) => {const next=event.target.value;if(next!==program){setProgram(next);invalidateGuidanceTerms(discountBps,next)}}}
                  >
                    {PROGRAM_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {program !== "NONE" && quote?.programMaxBps != null && (
                    <small>
                      Program maximum {(quote.programMaxBps / 100).toFixed(1)}%
                    </small>
                  )}
                </label>
                <label className="control-block discount-input">
                  <span className="control-label">DISCOUNT %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={discountPercentValue}
                    placeholder="0.0"
                    onChange={(event) => {const next=event.target.value,nextBps=Math.round(Number(next||0)*100);setDiscountPercentValue(next);if(nextBps!==discountBps)invalidateGuidanceTerms(nextBps,program)}}
                  />
                  {quote && (
                    <small>
                      Standard maximum {(quote.standardMaxBps / 100).toFixed(1)}%
                    </small>
                  )}
                </label>
              </div>

              <div className="econ-quote" aria-live="polite">
                {onTermsChanged && <button type="button" className="secondary-btn" style={{minHeight:44}} disabled={!currentQuoteReady} onClick={confirmGuidanceQuote}>Confirm current quote in guidance</button>}
                {quoteLoading && <small>Quoting current terms…</small>}
                {quoteErrorCode && (
                  <div className="runtime-banner issue" role="alert">
                    <AlertTriangle size={16} />
                    <span>
                      {QUOTE_ERROR_COPY[quoteErrorCode] ??
                        `Quote unavailable (${quoteErrorCode.replaceAll("_", " ")}).`}
                    </span>
                  </div>
                )}
                {quoteErrorCode === "AGREEMENT_REQUIRED" && (
                  <div className="agreement-action">
                    <button
                      type="button"
                      className="secondary-btn"
                      disabled={agreementPending}
                      onClick={() => void requestCaseStudyAgreement()}
                    >
                      {agreementRequested
                        ? "Case study agreement requested"
                        : "Request case study agreement"}
                    </button>
                    {agreementRequested && (
                      <small>
                        Requested. The case study participation and data use must
                        be agreed before this program quotes.
                      </small>
                    )}
                  </div>
                )}
                {quote && !quoteErrorCode && (
                  <>
                    <div className="econ-grid">
                      <div>
                        <span>STANDARD PRICE</span>
                        <strong>{formatCents(quote.listTotalCents)}/mo</strong>
                      </div>
                      {program !== "NONE" && (
                        <div className="econ-program-line">
                          <span>PROGRAM</span>
                          <strong>
                            {PROGRAM_OPTIONS.find(
                              ([value]) => value === program,
                            )?.[1] ?? program}
                          </strong>
                        </div>
                      )}
                      <div>
                        <span>Discount</span>
                        <strong>-{formatCents(quote.discountCents)}</strong>
                      </div>
                      <div>
                        <span>NET RECURRING</span>
                        <strong>{formatCents(quote.netCents)}/mo</strong>
                      </div>
                      <div>
                        <span>YOUR EFFECTIVE BASE COMMISSION</span>
                        <strong>
                          {(quote.baseCommissionBps / 100).toFixed(2)}%
                        </strong>
                      </div>
                      <div>
                        <span>YOUR ADJUSTED COMMISSION</span>
                        <strong>
                          {(quote.effectiveRateBps / 100).toFixed(2)}%
                        </strong>
                      </div>
                      <div>
                        <span>ESTIMATED COMMISSION</span>
                        <strong>
                          {formatCents(quote.adjustedCommissionCents)}
                        </strong>
                      </div>
                      <div>
                        <span>COMPANY RETAINED</span>
                        <strong>{formatCents(quote.companyRetainedCents)}</strong>
                      </div>
                      <div className="econ-approval-line">
                        <span>APPROVAL</span>
                        <strong data-required-tier={quote.requiredTier}>
                          {REQUIRED_TIER_LABEL[quote.requiredTier] ??
                            `APPROVAL ${quote.requiredTier}`}
                        </strong>
                      </div>
                    </div>
                    <ReceiptText size={13} className="econ-watermark" />
                  </>
                )}
              </div>

              {approvalRequired && !approvalStatus && boundOpportunity && (
                <div className="close-request-row">
                  <label>
                    Reason for discount
                    <input
                      value={reason}
                      maxLength={2000}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Why these terms are right for this deal"
                    />
                  </label>
                  <label>
                    Supporting note (optional)
                    <textarea
                      value={supportingNote}
                      maxLength={4000}
                      rows={2}
                      onChange={(event) => setSupportingNote(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={
                      submitting ||
                      !quote ||
                      Boolean(quoteErrorCode) ||
                      reason.trim().length === 0
                    }
                    onClick={() => submitApprovalRequest()}
                  >
                    <BadgeCheck size={15} />
                    {submitting ? "Requesting…" : "Request approval"}
                  </button>
                </div>
              )}

              {approvalRequired && ackRequired && (
                <div className="ack-banner" role="status">
                  <span>
                    Read and acknowledge the Commercial Terms, Discount &
                    Commission Policy before requesting approval.
                  </span>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => void acknowledgeThenRetry()}
                  >
                    Acknowledge policy
                  </button>
                  <Link to="/sales-knowledge" className="policy-link">
                    View Commercial Policy
                  </Link>
                </div>
              )}

              {approvalRequired && requestErrorCode && !ackRequired && (
                <div className="runtime-banner issue" role="alert">
                  <AlertTriangle size={16} />
                  <span>
                    {REQUEST_ERROR_COPY[requestErrorCode] ??
                      `The approval request was rejected (${requestErrorCode.replaceAll("_", " ")}).`}
                  </span>
                </div>
              )}

              {approvalRequired &&
                approvalStatus === "PENDING_APPROVAL" &&
                !decidedRow && (
                <div className="approval-strip pending" role="status">
                  <Clock size={14} />
                  <span>
                    Approval pending — this workspace checks for a decision every
                    10 seconds.
                  </span>
                </div>
              )}

              {approvalRequired &&
                termsDrift &&
                approvalStatus === "APPROVED" && (
                <div className="runtime-banner issue" role="alert">
                  <AlertTriangle size={16} />
                  <span>
                    Commercial terms changed — new approval required
                  </span>
                </div>
              )}

              {approvalRequired &&
                approvalStatus === "APPROVED" &&
                liveApproval &&
                !termsDrift && (
                <div className="approved-panel" role="status">
                  <CheckCircle2 size={16} />
                  <div>
                    <strong>
                      Approved{" "}
                      {liveApproval.decidedAt
                        ? `at ${new Date(liveApproval.decidedAt).toLocaleString()}`
                        : ""}
                    </strong>
                    <span>
                      Expires{" "}
                      {new Date(liveApproval.expiresAt).toLocaleString()} ·{" "}
                      {formatCents(liveApproval.netCents)}/mo customer price
                    </span>
                  </div>
                </div>
              )}

              {approvalRequired &&
                (approvalStatus === "REJECTED" ||
                  approvalStatus === "CHANGES_REQUESTED") && (
                <div className="approval-strip blocked" role="alert">
                  <AlertTriangle size={14} />
                  <span>
                    {approvalStatus === "REJECTED"
                      ? "The requested discount was rejected."
                      : "Changes were requested before approval."}
                    {decidedRow?.decisionNote
                      ? ` Note: ${decidedRow.decisionNote}`
                      : ""}{" "}
                    Adjust the terms and request again.
                  </span>
                </div>
              )}
              {approvalRequired && approvalStatus === "EXPIRED" && (
                <div className="approval-strip blocked" role="alert">
                  <Clock size={14} />
                  <span>
                    This approval expired without issuance. Submit a fresh
                    request under current terms.
                  </span>
                </div>
              )}

              <div
                className={`commercial-readiness ${readiness.ready ? "ready" : "blocked"}`}
                role="status"
                data-commercial-readiness={readiness.label}
              >
                <div>
                  <strong>
                    {readiness.ready ? "Ready to close" : "Action required"}
                  </strong>
                  {readiness.ready ? (
                    <span>
                      {approvalRequired
                        ? "Approved terms are current."
                        : "No approval is required under the current policy."}
                    </span>
                  ) : (
                    <ul>
                      {readiness.requirements.map((requirement) => (
                        <li key={requirement}>{requirement}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {readiness.ready && (
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={issuing}
                    onClick={() => void issueCheckout()}
                  >
                    {issuing
                      ? "Issuing secure link…"
                      : "Issue secure payment link"}
                  </button>
                )}
              </div>

              {issueErrorCode && (
                <div className="runtime-banner issue" role="alert">
                  <AlertTriangle size={16} />
                  <span>
                    {ISSUE_ERROR_COPY[issueErrorCode] ??
                      `The secure link could not be issued (${issueErrorCode.replaceAll("_", " ")}). The same attempt can be retried.`}
                  </span>
                </div>
              )}

              {issuedUrl && checkoutSession && (
                <div className="secure-link-box">
                  <strong>Secure payment link</strong>
                  <div className="secure-link-row">
                    <input readOnly value={issuedUrl} aria-label="Secure payment link" onFocus={(event) => event.currentTarget.select()} />
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => void copyIssuedUrl()}
                    >
                      <ClipboardCopy size={14} /> {copied ? "Copied" : "Copy"}
                    </button>
                    <a
                      className="secondary-btn"
                      href={issuedUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <ExternalLink size={14} /> Open
                    </a>
                  </div>
                  {copyFailed && (
                    <p role="alert">
                      Clipboard access was blocked. Select the link text and copy
                      it manually.
                    </p>
                  )}
                  <p>
                    Reference {maskedReference} · Plan {plan}
                  </p>
                  {expiryState === "ACTIVE" && (
                    <p role="status">
                      <Clock size={13} /> Expires in{" "}
                      {formatCountdown(remainingSeconds)}
                    </p>
                  )}
                  {expiryState === "EXPIRED" && (
                    <p role="alert">
                      Expired. This checkout window closed. Issue a new link.
                    </p>
                  )}
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      attemptRef.current = resetCheckoutAttempt(
                        crypto.randomUUID(),
                      );
                      setIssuedUrl(null);
                      setCheckoutSession(null);
                      setIssueErrorCode(null);
                    }}
                  >
                    Start next checkout
                  </button>
                </div>
              )}

              <div className="take-payment-chip" role="note">
                <strong>TAKE PAYMENT</strong>
                <p>{PROVIDER_PENDING_BANNER}</p>
                <span className="status-chip">
                  NOT HEALTHY — AWAITING PROVIDER
                </span>
                {takePaymentState === "AVAILABLE" && (
                  <small>
                    Provider configuration changed; reopen this panel to refresh
                    capability truth.
                  </small>
                )}
              </div>
            </>
          )}
        </div>
      </details>
    </section>
  );
}
